import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { ROUTES_PATH } from '../../constants/Routes';
import { UserPreferences } from '../../services/userPreferences/types';
import { OnboardingOfficeStatus } from './types';

export function useOnboardingOffice() {
  const history = useHistory();
  const {
    isEligible,
    completed: onboardingProfileCompleted,
    isSubscriptionLoading,
    isPollingCredits,
    flagResolved,
    isPreferencesLoading,
    updateUserPreferencesAsync,
    preferences,
  } = useNewOnboardingEligibility('onboardingProfileCompleted');

  const oabSkipped = Boolean((preferences as UserPreferences).onboardingOabSkipped);
  const oabCompleted = Boolean(
    (preferences as UserPreferences).onboardingOabCompleted,
  );
  const photoCompleted = Boolean(
    (preferences as UserPreferences).onboardingPhotoCompleted,
  );
  const stepCompleted = oabSkipped
    ? photoCompleted || onboardingProfileCompleted
    : onboardingProfileCompleted;
  const oabStepDone = oabCompleted || oabSkipped;

  const status: OnboardingOfficeStatus = useMemo(() => {
    if (isSubscriptionLoading || !flagResolved || isPreferencesLoading) {
      return 'loading';
    }

    if (stepCompleted) {
      return 'completed';
    }

    if (!oabStepDone) {
      return 'prior_pending';
    }

    if (!isEligible) {
      return 'ineligible';
    }

    return 'ready';
  }, [
    isSubscriptionLoading,
    flagResolved,
    isPreferencesLoading,
    stepCompleted,
    oabStepDone,
    isEligible,
  ]);

  const markCompleted = useCallback(async () => {
    await updateUserPreferencesAsync({ onboardingProfileCompleted: true });
    history.push(ROUTES_PATH.MAIN.DASHBOARD);
  }, [updateUserPreferencesAsync, history]);

  return { status, isPollingCredits, markCompleted, oabSkipped };
}
